/**
 * Returns true if the trimmed line is the intro phrase ("Would you like me to:" or "**Would you like me to:**").
 * Uses string logic to avoid ReDoS-prone regex.
 */
function isIntroLine(line: string): boolean {
  const t = line.trim().toLowerCase()
  let s = t
  if (s.startsWith('**')) s = s.slice(2).trimStart()
  else if (s.startsWith('*')) s = s.slice(1).trimStart()
  if (s.endsWith('**')) s = s.slice(0, -2).trimEnd()
  else if (s.endsWith('*')) s = s.slice(0, -1).trimEnd()
  return s === 'would you like me to' || s === 'would you like me to:'
}

/**
 * Parses one line as a list item "1. [link text](#run-suggested-query...)". Returns link text or null.
 * Uses string logic to avoid ReDoS-prone regex.
 */
function parseListItemLine(line: string): string | null {
  const t = line.trim()
  let i = 0
  while (i < t.length && t[i] >= '0' && t[i] <= '9') i++
  if (i === 0 || i >= t.length || t[i] !== '.') return null
  i++
  while (i < t.length && (t[i] === ' ' || t[i] === '\t')) i++
  if (i >= t.length || t[i] !== '[') return null
  const bracketStart = i + 1
  const bracketEnd = t.indexOf(']', bracketStart)
  if (bracketEnd === -1) return null
  const parenStart = t.indexOf('(', bracketEnd)
  if (
    parenStart === -1 ||
    !t.slice(parenStart).startsWith('(#run-suggested-query')
  )
    return null
  return t.slice(bracketStart, bracketEnd)
}

/**
 * Returns true if the trimmed line looks like the start of a numbered list item (e.g. "1. [First").
 */
function isPartialListLine(line: string): boolean {
  const t = line.trim()
  let i = 0
  while (i < t.length && t[i] >= '0' && t[i] <= '9') i++
  if (i === 0 || i >= t.length || t[i] !== '.') return false
  i++
  while (i < t.length && (t[i] === ' ' || t[i] === '\t')) i++
  return i < t.length && t[i] === '['
}

export interface ParsedSuggestedPrompts {
  contentWithoutSection: string
  suggestedPrompts: string[]
}

function isHrChar(c: string): boolean {
  return c === '-' || c === '*' || c === '_'
}

/** Safe strip of trailing horizontal rule (--- or *** or ___) without ReDoS-prone regex. */
function stripTrailingHorizontalRule(s: string): string {
  let t = s.trimEnd()
  let j = t.length - 1
  while (j >= 0 && isHrChar(t[j])) j--
  const runLength = t.length - 1 - j
  if (runLength >= 3) t = t.slice(0, j + 1).trimEnd()
  return t
}

/** Safe strip of leading horizontal rule (--- or *** or ___) without ReDoS-prone regex. */
function stripLeadingHorizontalRule(s: string): string {
  let t = s.trimStart()
  let i = 0
  while (i < t.length && isHrChar(t[i])) i++
  if (i >= 3) t = t.slice(i).trimStart()
  return t
}

/**
 * Finds a contiguous block of numbered list items that are all #run-suggested-query links,
 * optionally preceded by an intro line like "Would you like me to:". Extracts the link texts
 * and removes the entire block from the content.
 */
export function parseSuggestedPromptsSection(
  content: string,
): ParsedSuggestedPrompts {
  if (typeof content !== 'string' || content.trim() === '') {
    return { contentWithoutSection: content, suggestedPrompts: [] }
  }

  const lines = content.split('\n')
  const { matches } = lines.reduce<{
    matches: { end: number; index: number; text: string }[]
    offset: number
  }>(
    (acc, line) => {
      const text = parseListItemLine(line)
      if (text !== null) {
        acc.matches.push({
          text,
          index: acc.offset,
          end: acc.offset + line.length,
        })
      }
      acc.offset += line.length + 1
      return acc
    },
    { offset: 0, matches: [] },
  )

  if (matches.length === 0) {
    return { contentWithoutSection: content, suggestedPrompts: [] }
  }

  // Check matches are contiguous (only whitespace/newlines between them)
  for (let i = 1; i < matches.length; i++) {
    const between = content.slice(matches[i - 1].end, matches[i].index)
    if (between.trim() !== '') {
      return { contentWithoutSection: content, suggestedPrompts: [] }
    }
  }

  const firstIndex = matches[0].index
  const lastEnd = matches[matches.length - 1].end

  // Section start: optional intro line before the list (e.g. "Would you like me to:" or "**Would you like me to:**")
  let sectionStart = firstIndex
  const trimmedBefore = content.slice(0, firstIndex).trimEnd()
  if (trimmedBefore.length > 0) {
    const lastLine =
      trimmedBefore.lastIndexOf('\n') >= 0
        ? trimmedBefore.slice(trimmedBefore.lastIndexOf('\n') + 1)
        : trimmedBefore
    if (isIntroLine(lastLine)) {
      sectionStart = trimmedBefore.lastIndexOf('\n') + 1
    }
  }

  let before = content.slice(0, sectionStart).trimEnd()
  // Remove a trailing horizontal rule (--- or *** or ___) that often precedes the section in markdown
  before = stripTrailingHorizontalRule(before)
  let after = content.slice(lastEnd).trimStart()
  // Remove a leading horizontal rule (--- or *** or ___) that often follows the section in markdown
  after = stripLeadingHorizontalRule(after)
  const separator = before.length > 0 && after.length > 0 ? '\n\n' : ''
  const contentWithoutSection = `${before}${separator}${after}`.trim()

  return {
    contentWithoutSection,
    suggestedPrompts: matches.map((x) => x.text),
  }
}

/**
 * Strips a partial suggested-prompts section from the end of content (for display during streaming).
 * If the intro line "Would you like me to:" or the start of a numbered list is present, removes
 * from that point to the end so the bubble never shows partial section text.
 */
export function stripPartialSuggestedPromptsSection(content: string): string {
  if (typeof content !== 'string' || content.trim() === '') {
    return content
  }
  const lines = content.split('\n')
  let sectionStartIndex: number | null = null
  for (let i = 0; i < lines.length; i++) {
    if (isIntroLine(lines[i])) {
      sectionStartIndex = i
      break
    }
  }
  if (sectionStartIndex === null) {
    // No intro; check if content ends with a line that looks like start of numbered list
    for (let i = lines.length - 1; i >= 0; i--) {
      const trimmed = lines[i].trim()
      if (trimmed !== '' && isPartialListLine(trimmed)) {
        sectionStartIndex = i
        break
      }
      if (trimmed !== '') break
    }
  }
  if (sectionStartIndex === null) {
    return content
  }
  let before =
    sectionStartIndex === 0
      ? ''
      : lines.slice(0, sectionStartIndex).join('\n').trimEnd()
  before = stripTrailingHorizontalRule(before)
  return before
}
